/**
 * `curve.*` commands (W2-04b curve-editor; WORKPACKAGES Appendix B; DESIGN.md §4.7, §8.2 "Expand / collapse curve
 * editor | G").
 *
 * `G` acts on the FOCUSED column (never the one under the mouse): it opens the curve editor on it, closes the
 * editor when it already shows that property, and moves the editor to the focused column otherwise. Every command
 * has a menu, palette or button path (curve editor header, `curve.context`, `motion.context`, View menu).
 *
 * The right-click menu of the editor (`curve.context`) offers the same key and segment actions as the header's
 * selection row (DESIGN.md §5.6.5: the context menu is a path for segment operations): Change curve, Stay then jump,
 * Make jump, Continue from previous, Bake, Reverse, Link and Delete. They are available while the editor is open
 * (`when: curve.expanded`); Bake, Reverse and Link run the batch package's contracted `segments.*` commands.
 */

import type { CommandArgs } from '../../app/registry/commands';
import type { CommandContext, CommandDef, KeyBinding, KeymapPresetId } from '../../app/registry/types';
import type { MessageRef } from '../../core/util/types';
import {
  addLayer,
  cannotAddLayer,
  cannotEditKeys,
  cannotEditSegments,
  cannotExpand,
  cannotMakeBezier,
  cannotMakeJump,
  cannotRemoveJump,
  changeCurve,
  collapseEditor,
  deleteKeys,
  editBezier,
  expandColumn,
  expandedTarget,
  flattenLayers,
  makeBezier,
  makeJump,
  removeJump,
  resetSlice,
  selectedBezier,
  selectedSegments,
  slicedSegments,
  switchLayer,
  toggleHold,
} from './curveActions';
import { liveCurveViews, shownProps } from './curveColumn';
import { m } from './curveEditor.messages';
import { isLayered, layerCount, parseMotionColumnId } from './curveModel';
import { readCurveSetting, toggleCurveSetting, type CurveSettingKey } from './curveSettings';

const ZOOM_STEP = 1.5;

function needEditor(ctx: CommandContext): true | MessageRef {
  if (ctx.store === null) return { key: m.noProject };
  return expandedTarget(ctx) === null ? { key: m.noCurveEditor } : true;
}

function argOf(args: unknown, key: string): unknown {
  return typeof args === 'object' && args !== null ? (args as Readonly<Record<string, unknown>>)[key] : undefined;
}

/**
 * The focused column is shown by the editor: the same line and a property it draws (the channel view focuses other
 * layers of the expanded property; the Guided Position pair shows X and Y).
 */
function showsProperty(ctx: CommandContext): boolean {
  const cur = expandedTarget(ctx);
  const focused = ctx.editor.getState().focusedColumn;
  if (cur === null) return false;
  return focused === null || (focused.line === cur.line && shownProps(ctx, cur).includes(focused.prop));
}

const CONTEXT = 'curve.context';
const EDITOR_OPEN = 'curve.expanded';

type BatchCommand = Extract<keyof CommandArgs, 'segments.bake' | 'segments.reverse' | 'segments.link'>;

/** A batch package segment command offered in the editor's context menu (same enabled state, same run). */
function batchWrapper(
  id: string,
  command: BatchCommand,
  title: CommandDef['title'],
  icon: string,
  order: number,
): CommandDef {
  return {
    id,
    title,
    category: 'Motion',
    icon,
    level: 'guided',
    when: EDITOR_OPEN,
    enabled(ctx) {
      const why = cannotEditSegments(ctx);
      if (why !== null) return why;
      const def = ctx.registry.command(command);
      if (def === undefined) return { key: m.toolMissing };
      return def.enabled?.(ctx) ?? true;
    },
    async run(ctx) {
      await ctx.registry.run(command, undefined, ctx.source);
    },
    menus: [{ menu: CONTEXT, group: 'edit', order }],
  };
}

function settingToggle(
  id: string,
  setting: CurveSettingKey,
  title: CommandDef['title'],
  icon: string,
  extra: Pick<CommandDef, 'enabled'> & { readonly relayout?: boolean } = {},
): CommandDef {
  return {
    id,
    title,
    category: 'View',
    icon,
    level: 'full',
    ...(extra.enabled === undefined ? {} : { enabled: extra.enabled }),
    checked: (ctx) => readCurveSetting(ctx.settings, setting),
    run(ctx) {
      toggleCurveSetting(ctx.settings, setting);
      if (extra.relayout === true) ctx.timeline?.relayout();
    },
    menus: [{ menu: 'curve.context', group: 'view', order: 10 }],
  };
}

export const CURVE_COMMANDS: readonly CommandDef[] = [
  {
    id: 'curve.toggle',
    title: m.cmdToggle,
    category: 'Motion',
    icon: 'ChartSpline',
    aliases: ['graph editor', 'expand column', 'curve', 'G', '曲线'],
    level: 'guided',
    hint: 40,
    enabled(ctx) {
      if (ctx.store === null) return { key: m.noProject };
      if (expandedTarget(ctx) !== null) return true;
      return cannotExpand(ctx, ctx.editor.getState().focusedColumn) ?? true;
    },
    checked: (ctx) => expandedTarget(ctx) !== null,
    run(ctx) {
      const focused = ctx.editor.getState().focusedColumn;
      if (expandedTarget(ctx) !== null && showsProperty(ctx)) {
        collapseEditor(ctx);
        return;
      }
      if (focused === null) {
        ctx.ui.toast({ key: m.noFocusedColumn }, { kind: 'info' });
        return;
      }
      expandColumn(ctx, focused);
    },
    menus: [
      { menu: 'motion.context', group: 'view', order: 10 },
      { menu: 'app.view', group: 'timeline', order: 40 },
    ],
  },
  {
    id: 'curve.expand',
    title: m.cmdExpand,
    category: 'Motion',
    icon: 'Expand',
    aliases: ['open curve editor', 'graph'],
    level: 'guided',
    enabled: (ctx) => (ctx.store === null ? { key: m.noProject } : true),
    run(ctx, args) {
      const given = parseMotionColumnId(
        typeof argOf(args, 'column') === 'string' ? (argOf(args, 'column') as string) : null,
      );
      const ref = given ?? ctx.editor.getState().focusedColumn;
      if (ref === null) {
        ctx.ui.toast({ key: m.noFocusedColumn }, { kind: 'info' });
        return;
      }
      expandColumn(ctx, ref);
    },
  },
  {
    id: 'curve.collapse',
    title: m.cmdCollapse,
    category: 'Motion',
    icon: 'X',
    level: 'guided',
    enabled: needEditor,
    run(ctx) {
      collapseEditor(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'close', order: 90 }],
  },
  settingToggle('curve.channelView', 'channel', m.cmdChannelView, 'Columns3', {
    relayout: true,
    enabled(ctx) {
      const cur = expandedTarget(ctx);
      if (cur === null) return { key: m.noCurveEditor };
      if (!isLayered(cur.prop)) return { key: m.channelNeedsLayers };
      if (ctx.editor.getState().level !== 'full') return { key: m.channelFullOnly };
      return true;
    },
  }),
  settingToggle('curve.sigma', 'sigma', m.cmdSigma, 'Sigma'),
  settingToggle('curve.ghosts', 'ghosts', m.cmdGhosts, 'Ghost'),
  settingToggle('curve.distance', 'distance', m.cmdDistance, 'Ruler', {
    enabled(ctx) {
      const cur = expandedTarget(ctx);
      return cur === null || cur.prop === 'speed' ? true : { key: m.speedOnly };
    },
  }),
  {
    id: 'curve.fitValues',
    title: m.cmdFitValues,
    category: 'View',
    icon: 'Scan',
    aliases: ['auto fit', 'reset value zoom'],
    level: 'guided',
    enabled: needEditor,
    run() {
      for (const v of liveCurveViews()) v.fitValues();
    },
    menus: [{ menu: 'curve.context', group: 'view', order: 20 }],
  },
  {
    id: 'curve.zoomValuesIn',
    title: m.cmdZoomValuesIn,
    category: 'View',
    icon: 'ZoomIn',
    level: 'guided',
    enabled: needEditor,
    run() {
      for (const v of liveCurveViews()) v.zoomValues(ZOOM_STEP);
    },
  },
  {
    id: 'curve.zoomValuesOut',
    title: m.cmdZoomValuesOut,
    category: 'View',
    icon: 'ZoomOut',
    level: 'guided',
    enabled: needEditor,
    run() {
      for (const v of liveCurveViews()) v.zoomValues(1 / ZOOM_STEP);
    },
  },
  {
    id: 'curve.flatten',
    title: m.cmdFlatten,
    category: 'Motion',
    icon: 'Merge',
    aliases: ['flatten layers', 'merge layers'],
    level: 'full',
    enabled(ctx) {
      const cur = expandedTarget(ctx);
      const store = ctx.store;
      if (cur === null || store === null) return { key: m.noCurveEditor };
      if (!isLayered(cur.prop) || layerCount(store.doc, store.idx, cur.line, cur.prop) < 2)
        return { key: m.flattenNothing };
      if (ctx.editor.getState().filters.lockedLines.has(cur.line)) return { key: m.lineLocked };
      return true;
    },
    async run(ctx) {
      await flattenLayers(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'layers', order: 20 }],
  },
  {
    id: 'curve.makeBezier',
    title: m.cmdMakeBezier,
    category: 'Motion',
    icon: 'PenTool',
    aliases: ['bezier', 'custom curve', 'cubic-bezier'],
    level: 'full',
    enabled: (ctx) => cannotMakeBezier(ctx) ?? true,
    run(ctx) {
      makeBezier(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'curve', order: 10 }],
  },
  {
    id: 'curve.editBezier',
    title: m.cmdEditBezier,
    category: 'Motion',
    icon: 'Spline',
    level: 'full',
    enabled: (ctx) => (selectedBezier(ctx) === null ? { key: m.noBezierSegment } : true),
    async run(ctx) {
      await editBezier(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'curve', order: 20 }],
  },
  {
    id: 'curve.resetSlice',
    title: m.cmdResetSlice,
    category: 'Motion',
    icon: 'Maximize2',
    aliases: ['easingLeft', 'easingRight', 'clip', 'slice'],
    level: 'full',
    enabled: (ctx) => (slicedSegments(ctx).length === 0 ? { key: m.noSlicedSegments } : true),
    run(ctx) {
      resetSlice(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'curve', order: 30 }],
  },
  {
    id: 'curve.addLayer',
    title: m.cmdAddLayer,
    category: 'Motion',
    icon: 'Layers',
    level: 'full',
    enabled: (ctx) => cannotAddLayer(ctx, expandedTarget(ctx)) ?? true,
    run(ctx) {
      addLayer(ctx);
    },
    menus: [{ menu: 'curve.context', group: 'layers', order: 10 }],
  },
  // ---- key and segment actions of the context menu (same as the header's selection row) ----
  {
    id: 'curve.changeCurve',
    title: m.btnChangeCurve,
    category: 'Motion',
    icon: 'Spline',
    aliases: ['easing', 'curve type'],
    level: 'guided',
    when: EDITOR_OPEN,
    enabled: (ctx) => cannotEditSegments(ctx) ?? true,
    async run(ctx) {
      await changeCurve(ctx, selectedSegments(ctx));
    },
    menus: [{ menu: CONTEXT, group: 'edit', order: 1 }],
  },
  {
    id: 'curve.hold',
    title: m.btnHold,
    category: 'Motion',
    icon: 'StepForward',
    aliases: ['stay', 'hold'],
    level: 'guided',
    when: EDITOR_OPEN,
    enabled: (ctx) => cannotEditSegments(ctx) ?? true,
    run(ctx) {
      toggleHold(ctx);
    },
    menus: [{ menu: CONTEXT, group: 'edit', order: 2 }],
  },
  {
    id: 'curve.makeJump',
    title: m.btnJump,
    category: 'Motion',
    icon: 'ArrowUpFromLine',
    aliases: ['jump'],
    level: 'guided',
    when: EDITOR_OPEN,
    enabled: (ctx) => cannotMakeJump(ctx) ?? true,
    run(ctx) {
      makeJump(ctx);
    },
    menus: [{ menu: CONTEXT, group: 'edit', order: 3 }],
  },
  {
    id: 'curve.removeJump',
    title: m.btnRemoveJump,
    category: 'Motion',
    icon: 'CornerDownRight',
    aliases: ['stick', 'remove jump'],
    level: 'guided',
    when: EDITOR_OPEN,
    enabled: (ctx) => cannotRemoveJump(ctx) ?? true,
    run(ctx) {
      removeJump(ctx);
    },
    menus: [{ menu: CONTEXT, group: 'edit', order: 4 }],
  },
  batchWrapper('curve.bake', 'segments.bake', m.btnBake, 'Grid3x3', 5),
  batchWrapper('curve.reverse', 'segments.reverse', m.cmdReverse, 'ArrowUpDown', 6),
  batchWrapper('curve.link', 'segments.link', m.cmdLink, 'Link', 7),
  {
    id: 'curve.deleteKeys',
    title: m.cmdDeleteKeys,
    category: 'Motion',
    icon: 'Trash2',
    aliases: ['remove keys'],
    level: 'guided',
    when: EDITOR_OPEN,
    enabled: (ctx) => cannotEditKeys(ctx) ?? true,
    run(ctx) {
      deleteKeys(ctx);
    },
    menus: [{ menu: CONTEXT, group: 'delete', order: 85 }],
  },
  {
    id: 'curve.layer',
    title: m.cmdLayer,
    category: 'Motion',
    icon: 'Layers',
    level: 'full',
    args: { kind: 'number', label: { key: m.argLayer }, min: 0, max: 31 },
    enabled(ctx) {
      const cur = expandedTarget(ctx);
      if (cur === null) return { key: m.noCurveEditor };
      return isLayered(cur.prop) ? true : { key: m.channelNeedsLayers };
    },
    run(ctx, args) {
      const raw = typeof args === 'number' ? args : argOf(args, 'layer');
      if (typeof raw === 'number') switchLayer(ctx, Math.trunc(raw));
    },
  },
];

const G: KeyBinding = { chord: 'KeyG', command: 'curve.toggle', when: 'workspace == chart' };

/** `G` in both presets (RPE has no conflicting G binding; Ctrl+G is Fill there). */
export const CURVE_KEYBINDINGS: Readonly<Record<KeymapPresetId, readonly KeyBinding[]>> = {
  default: [G],
  rpe: [G],
};
